import React, { FC, useCallback, useMemo, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Collapse from '@mui/material/Collapse'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import MuiTextField from '@mui/material/TextField'
import { TextField } from '../atoms'
import { IoSearch, IoChevronDown, IoChevronUp, IoCheckmarkCircle, IoCloseCircle } from 'react-icons/io5'
import { primaryIconSize, theme } from '../../lib/styles/universal'
import { makeStyles } from 'tss-react/mui'
import { InterestedOrganizationAdmin } from '@hazadapt-git/public-core-base'
import {
    INTEREST_COMMUNITY_SIZE_OPTIONS,
    INTEREST_REFERRAL_SOURCE_OPTIONS,
    formatInterestSubscriptionLabel,
} from '../../lib/utils'

interface InterestedOrganizationsTableProps {
    submissions: InterestedOrganizationAdmin[]
    onSaveNotes: (
        id: string,
        payload: { internal_notes: string | null }
    ) => Promise<void>
}

export const InterestedOrganizationsTable: FC<
    InterestedOrganizationsTableProps
> = ({ submissions, onSaveNotes }) => {
    const { classes: localClasses } = useLocalStyles()
    const [searchQuery, setSearchQuery] = useState('')
    const [expandedId, setExpandedId] = useState<string | null>(null)
    const [internalNotesValues, setInternalNotesValues] = useState<
        Record<string, string>
    >({})
    const [pendingInternalNotesSaveIds, setPendingInternalNotesSaveIds] =
        useState<Set<string>>(new Set())

    const getInternalNotes = useCallback(
        (submission: InterestedOrganizationAdmin) =>
            internalNotesValues[submission.id] ??
            submission.internal_notes ??
            '',
        [internalNotesValues]
    )

    const handleSaveNotes = useCallback(
        async (submission: InterestedOrganizationAdmin) => {
            setPendingInternalNotesSaveIds((prev) =>
                new Set(prev).add(submission.id)
            )
            try {
                await onSaveNotes(submission.id, {
                    internal_notes:
                        internalNotesValues[submission.id] ??
                        submission.internal_notes ??
                        null,
                })
            } finally {
                setPendingInternalNotesSaveIds((prev) => {
                    const next = new Set(prev)
                    next.delete(submission.id)
                    return next
                })
            }
        },
        [internalNotesValues, onSaveNotes]
    )

    const getCommunitySizeLabel = useCallback(
        (communitySize: InterestedOrganizationAdmin['community_size']) => {
            if (!communitySize) return null
            return (
                INTEREST_COMMUNITY_SIZE_OPTIONS.find(
                    (option) => option.value === communitySize
                )?.label ?? communitySize
            )
        },
        []
    )

    const getReferralSourceLabel = useCallback(
        (referralSource: InterestedOrganizationAdmin['referral_source']) => {
            if (!referralSource) return '—'
            return (
                INTEREST_REFERRAL_SOURCE_OPTIONS.find(
                    (option) => option.value === referralSource
                )?.label ?? referralSource
            )
        },
        []
    )

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return submissions
        const query = searchQuery.trim().toLowerCase()
        return submissions.filter((submission) => {
            return (
                submission.organization_name?.toLowerCase().includes(query) ||
                submission.contact_name.toLowerCase().includes(query) ||
                submission.official_email.toLowerCase().includes(query) ||
                submission.jurisdiction_served?.toLowerCase().includes(query) ||
                submission.website_url?.toLowerCase().includes(query)
            )
        })
    }, [submissions, searchQuery])

    const toggleRow = (id: string) => {
        setExpandedId((prev) => (prev === id ? null : id))
    }

    return (
        <Stack gap="1rem" className={localClasses.container}>
            <TextField
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                    startAdornment: <IoSearch size={primaryIconSize} />,
                    placeholder: 'Search by name, email, or jurisdiction',
                }}
                sx={{ maxWidth: '32rem' }}
            />
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width="3%" />
                        <TableCell>
                            <Typography fontWeight={600}>Organization</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight={600}>Contact</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight={600}>Email</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight={600}>
                                Jurisdiction
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography fontWeight={600}>Submitted</Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filtered.length ? (
                        filtered.map((submission) => (
                            <React.Fragment key={submission.id}>
                                <TableRow hover>
                                    <TableCell padding="checkbox">
                                        <IconButton
                                            aria-label={
                                                expandedId === submission.id
                                                    ? 'Hide submission details'
                                                    : 'Show submission details'
                                            }
                                            onClick={() =>
                                                toggleRow(submission.id)
                                            }
                                            size="small"
                                        >
                                            {expandedId === submission.id ? (
                                                <IoChevronUp />
                                            ) : (
                                                <IoChevronDown />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <Typography fontWeight={500}>
                                            {submission.organization_name}
                                        </Typography>
                                        {submission.community_size ? (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {getCommunitySizeLabel(
                                                    submission.community_size
                                                )}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        {submission.contact_name}
                                    </TableCell>
                                    <TableCell>
                                        {submission.official_email}
                                    </TableCell>
                                    <TableCell>
                                        {submission.jurisdiction_served ?? '—'}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const submittedAt =
                                                submission.date_created instanceof
                                                Date
                                                    ? submission.date_created
                                                    : new Date(
                                                          submission.date_created
                                                      )
                                            return submittedAt.toLocaleDateString()
                                        })()}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        style={{
                                            paddingBottom: 0,
                                            paddingTop: 0,
                                        }}
                                        colSpan={6}
                                    >
                                        <Collapse
                                            in={expandedId === submission.id}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <Box
                                                padding="1rem 1.5rem"
                                                bgcolor={theme.palette.grey[50]}
                                            >
                                                <Stack gap="1.5rem">
                                                    <Stack
                                                        direction={{
                                                            xs: 'column',
                                                            md: 'row',
                                                        }}
                                                        spacing={{
                                                            xs: 1,
                                                            md: 4,
                                                        }}
                                                    >
                                                        <DetailItem
                                                            label="Website"
                                                            content={
                                                                submission.website_url ? (
                                                                    <Link
                                                                        href={
                                                                            submission.website_url
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {
                                                                            submission.website_url
                                                                        }
                                                                    </Link>
                                                                ) : (
                                                                    '—'
                                                                )
                                                            }
                                                        />
                                                        <DetailItem
                                                            label="Payment on File"
                                                            content={
                                                                submission.stripe_customer_id ? (
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        gap="0.25rem"
                                                                    >
                                                                        <IoCheckmarkCircle
                                                                            color={
                                                                                theme.palette.success.main
                                                                            }
                                                                        />
                                                                        <Typography variant="body2">
                                                                            Stripe customer saved
                                                                        </Typography>
                                                                    </Stack>
                                                                ) : (
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        gap="0.25rem"
                                                                    >
                                                                        <IoCloseCircle
                                                                            color={
                                                                                theme.palette.grey[500]
                                                                            }
                                                                        />
                                                                        <Typography variant="body2">
                                                                            No payment method yet
                                                                        </Typography>
                                                                    </Stack>
                                                                )
                                                            }
                                                        />
                                                            <DetailItem
                                                                label="Referral Source"
                                                                content={getReferralSourceLabel(
                                                                    submission.referral_source
                                                                )}
                                                            />
                                                    </Stack>
                                                    <Divider />
                                                    <Stack
                                                        direction={{
                                                            xs: 'column',
                                                            md: 'row',
                                                        }}
                                                        spacing={{
                                                            xs: 1,
                                                            md: 4,
                                                        }}
                                                    >
                                                        <DetailItem
                                                            label="Details"
                                                            content={
                                                                submission.details ??
                                                                '—'
                                                            }
                                                        />
                                                    </Stack>
                                                    <Divider />
                                                    <Stack gap="1rem">
                                                        <Stack
                                                            direction={{
                                                                xs: 'column',
                                                                md: 'row',
                                                            }}
                                                            spacing={{
                                                                xs: 1,
                                                                md: 4,
                                                            }}
                                                        >
                                                            <DetailItem
                                                                label="Subscription"
                                                                content={
                                                                    submission.subscription_type
                                                                        ? formatInterestSubscriptionLabel(
                                                                              submission.subscription_type
                                                                          )
                                                                        : '—'
                                                                }
                                                            />
                                                            <DetailItem
                                                                label="Pricing Quote"
                                                                content={
                                                                    submission.pricing_quote ??
                                                                    '—'
                                                                }
                                                            />
                                                        </Stack>
                                                        <Stack gap="0.5rem">
                                                            <Typography
                                                                variant="subtitle2"
                                                                color="text.secondary"
                                                            >
                                                                Internal Notes
                                                            </Typography>
                                                            <MuiTextField
                                                                multiline
                                                                minRows={3}
                                                                maxRows={8}
                                                                fullWidth
                                                                placeholder="Add internal notes about this submission..."
                                                                value={getInternalNotes(
                                                                    submission
                                                                )}
                                                                onChange={(e) =>
                                                                    setInternalNotesValues(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            [submission.id]:
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                        })
                                                                    )
                                                                }
                                                                size="small"
                                                            />
                                                        </Stack>
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="flex-end"
                                                        >
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                disabled={
                                                                    pendingInternalNotesSaveIds.has(
                                                                        submission.id
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    handleSaveNotes(
                                                                        submission
                                                                    )
                                                                }
                                                            >
                                                                {pendingInternalNotesSaveIds.has(
                                                                    submission.id
                                                                )
                                                                    ? 'Saving...'
                                                                    : 'Save Notes'}
                                                            </Button>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                            </TableRow>
                            </React.Fragment>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Typography
                                    textAlign="center"
                                    color="text.secondary"
                                >
                                    No submissions found.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
                </Stack>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        width: '100%',
        paddingBottom: '2rem',
        [theme.breakpoints.down('md')]: {
            overflowX: 'auto',
        },
    },
})

interface DetailItemProps {
    label: string
    content: React.ReactNode
}

const DetailItem: FC<DetailItemProps> = ({ label, content }) => (
    <Stack spacing={0.25} flex={1} minWidth={{ xs: '100%', md: '12rem' }}>
        <Typography variant="subtitle2" color="text.secondary">
            {label}
        </Typography>
        <Typography component="div">{content}</Typography>
    </Stack>
)
